import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { 
  FaReact, FaHtml5, FaCss3Alt, FaJs, FaNodeJs, FaGitAlt, FaPython,
  FaDatabase, FaWordpress, FaShopify, FaSass, FaBootstrap, FaDocker,
  FaAws, FaGit, FaFigma, FaChrome, FaMobileAlt, FaServer, FaShoppingCart, FaAward
} from 'react-icons/fa';
import BookAppointment from '../components/BookAppointment';

const SkillsSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const SkillsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 1rem;
    color: #ffffff;
  }
  
  p {
    font-size: 1.2rem;
    color: #cccccc;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const SkillsCategories = styled.div`
  display: grid;
  gap: 3rem;
`;

const SkillCategory = styled(motion.div)`
  background: #1a1a1a;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(70, 130, 180, 0.1);
`;

const CategoryTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .icon {
    color: #4682B4;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
`;

const SkillItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
  background: #2a2a2a;
  border-radius: 15px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(70, 130, 180, 0.1);
  
  &:hover {
    transform: translateY(-5px);
    background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
    color: #000000;
    border-color: rgba(70, 130, 180, 0.3);
    
    .skill-icon {
      color: #000000;
    }
    
    .skill-name,
    .skill-level {
      color: #000000;
    }
  }
`;

const SkillIcon = styled.div`
  font-size: 3rem;
  color: #4682B4;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
`;

const SkillName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
`;

const SkillLevel = styled.div`
  font-size: 0.875rem;
  color: #cccccc;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const SkillBar = styled.div`
  width: 100%;
  height: 8px;
  background: #333333;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const SkillProgress = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4682B4, #87CEEB);
  border-radius: 4px;
  transform-origin: left;
`;

const ToolsSection = styled.div`
  margin-top: 3rem;
  background: #1a1a1a;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(70, 130, 180, 0.1);
`;

const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ToolItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: #2a2a2a;
  border-radius: 10px;
  font-weight: 500;
  color: #cccccc;
  transition: all 0.3s ease;
  border: 1px solid rgba(70, 130, 180, 0.1);
  
  &:hover {
    background: #4682B4;
    color: #000000;
    transform: translateY(-3px);
    border-color: #4682B4;
  }
`;

const ExperienceSection = styled.div`
  margin-top: 3rem;
  background: #1a1a1a;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(70, 130, 180, 0.1);
`;

const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ExperienceItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
  
  .years {
    font-size: 2.5rem;
    font-weight: 700;
    color: #4682B4;
    margin-bottom: 0.5rem;
  }
  
  .category {
    font-size: 1.1rem;
    color: #ffffff;
    font-weight: 600;
  }
  
  .description {
    font-size: 0.9rem;
    color: #cccccc;
    margin-top: 0.5rem;
  }
`;

const Skills = () => {
  const skillCategories = [
    {
      title: "Frontend Development",
      icon: <FaReact style={{ color: '#61DAFB' }} />,
      skills: [
        { name: "React.js", level: "Expert", percentage: 95, icon: <FaReact style={{ color: '#61DAFB' }} /> },
        { name: "JavaScript", level: "Expert", percentage: 95, icon: <FaJs style={{ color: '#F7DF1E' }} /> },
        { name: "HTML5", level: "Expert", percentage: 98, icon: <FaHtml5 style={{ color: '#E34C26' }} /> },
        { name: "CSS3", level: "Expert", percentage: 95, icon: <FaCss3Alt style={{ color: '#1572B6' }} /> },
        { name: "Sass/SCSS", level: "Advanced", percentage: 85, icon: <FaSass style={{ color: '#CF649A' }} /> },
        { name: "Bootstrap", level: "Advanced", percentage: 80, icon: <FaBootstrap style={{ color: '#7952B3' }} /> }
      ]
    },
    {
      title: "Backend Development",
      icon: <FaServer style={{ color: '#FF5722' }} />,
      skills: [
        { name: "Node.js", level: "Advanced", percentage: 85, icon: <FaNodeJs style={{ color: '#339933' }} /> },
        { name: "Python", level: "Intermediate", percentage: 70, icon: <FaPython style={{ color: '#3776AB' }} /> },
        { name: "Express.js", level: "Advanced", percentage: 80, icon: <FaServer style={{ color: '#FF5722' }} /> },
        { name: "REST APIs", level: "Expert", percentage: 90, icon: <FaServer style={{ color: '#FF5722' }} /> },
        { name: "GraphQL", level: "Intermediate", percentage: 65, icon: <FaServer style={{ color: '#FF5722' }} /> },
        { name: "MongoDB", level: "Advanced", percentage: 75, icon: <FaDatabase style={{ color: '#4DB33C' }} /> }
      ]
    },
    {
      title: "CMS & E-commerce",
      icon: <FaWordpress style={{ color: '#21759B' }} />,
      skills: [
        { name: "WordPress", level: "Expert", percentage: 90, icon: <FaWordpress style={{ color: '#21759B' }} /> },
        { name: "Shopify", level: "Advanced", percentage: 85, icon: <FaShopify style={{ color: '#FFBF00' }} /> },
        { name: "WooCommerce", level: "Advanced", percentage: 80, icon: <FaShoppingCart style={{ color: '#FFBF00' }} /> },
        { name: "Custom Themes", level: "Expert", percentage: 88, icon: <FaWordpress style={{ color: '#21759B' }} /> },
        { name: "Plugin Dev", level: "Advanced", percentage: 75, icon: <FaWordpress style={{ color: '#21759B' }} /> },
        { name: "PHP", level: "Intermediate", percentage: 70, icon: <FaServer style={{ color: '#FF5722' }} /> }
      ]
    },
    {
      title: "Tools & Technologies",
      icon: <FaGitAlt style={{ color: '#F05032' }} />,
      skills: [
        { name: "Git", level: "Expert", percentage: 90, icon: <FaGit style={{ color: '#F05032' }} /> },
        { name: "Docker", level: "Intermediate", percentage: 65, icon: <FaDocker style={{ color: '#2496ED' }} /> },
        { name: "AWS", level: "Intermediate", percentage: 60, icon: <FaAws style={{ color: '#FF9900' }} /> },
        { name: "Figma", level: "Advanced", percentage: 75, icon: <FaFigma style={{ color: '#F24E1E' }} /> },
        { name: "Chrome DevTools", level: "Expert", percentage: 92, icon: <FaChrome style={{ color: '#4285F4' }} /> },
        { name: "Responsive Design", level: "Expert", percentage: 95, icon: <FaMobileAlt style={{ color: '#02AABF' }} /> }
      ]
    }
  ];

  const additionalTools = [
    "VS Code", "Webpack", "Babel", "ESLint", "Prettier", "Jest",
    "React Router", "Redux", "TypeScript", "Tailwind CSS", "Styled Components",
    "Netlify", "Vercel", "Heroku", "MongoDB Atlas", "PostgreSQL", "MySQL"
  ];

  const experience = [
    { years: "5+", category: "Web Development", description: "Total years of professional experience" },
    { years: "3+", category: "React Development", description: "Specialized in React ecosystem" },
    { years: "4+", category: "E-commerce", description: "Building online stores" },
    { years: "2+", category: "Mobile Development", description: "React Native experience" }
  ];

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const progressVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        duration: 1,
        ease: "easeOut"
      }
    }
  };

  return (
    <SkillsSection>
      <SkillsContainer>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Technical Skills</h2>
          <p>
            A comprehensive overview of my technical expertise across different 
            areas of web development and related technologies.
          </p>
        </SectionHeader>

        <SkillsCategories>
          {skillCategories.map((category, categoryIndex) => (
            <SkillCategory
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              <CategoryTitle>
                <span className="icon">{category.icon}</span>
                {category.title}
              </CategoryTitle>
              <SkillsGrid>
                {category.skills.map((skill, skillIndex) => (
                  <SkillItem
                    key={skillIndex}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                  >
                    <SkillIcon className="skill-icon">{skill.icon}</SkillIcon>
                    <SkillName className="skill-name">{skill.name}</SkillName>
                    <SkillLevel className="skill-level">{skill.level}</SkillLevel>
                    <SkillBar>
                      <SkillProgress
                        variants={progressVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        style={{ width: `${skill.percentage}%` }}
                      />
                    </SkillBar>
                  </SkillItem>
                ))}
              </SkillsGrid>
            </SkillCategory>
          ))}
        </SkillsCategories>

        <ToolsSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <CategoryTitle>
            <span className="icon"><FaGitAlt /></span>
            Additional Tools & Technologies
          </CategoryTitle>
          <ToolsGrid>
            {additionalTools.map((tool, index) => (
              <ToolItem
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
              >
                {tool}
              </ToolItem>
            ))}
          </ToolsGrid>
        </ToolsSection>

        <ExperienceSection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <CategoryTitle>
            <span className="icon"><FaAward /></span>
            Experience Overview
          </CategoryTitle>
          <ExperienceGrid>
            {experience.map((exp, index) => (
              <ExperienceItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="years">{exp.years}</div>
                <div className="category">{exp.category}</div>
                <div className="description">{exp.description}</div>
              </ExperienceItem>
            ))}
          </ExperienceGrid>
        </ExperienceSection>
      </SkillsContainer>
      
      <div style={{ marginTop: '4rem' }}>
        <BookAppointment />
      </div>
    </SkillsSection>
  );
};

export default Skills;
